import { useEffect, useRef, useState } from "react";
import { CheckCircle2, CircleAlert, Info, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { setToastListener, type ToastVariant } from "@/lib/toast";

interface ToastItem {
  id: number;
  variant: ToastVariant;
  message: string;
}

// Per-variant styling, using the Garage Dark tokens so toasts match the app
// (and re-theme with light mode automatically).
const VARIANTS: Record<ToastVariant, { icon: typeof Info; cls: string; iconCls: string }> = {
  error: { icon: CircleAlert, cls: "border-red-border bg-red-bg text-red-ink", iconCls: "text-red-ink" },
  success: { icon: CheckCircle2, cls: "border-teal/40 bg-teal-bg text-teal-ink", iconCls: "text-teal-ink" },
  info: { icon: Info, cls: "border-edge-2 bg-surface-2 text-ink", iconCls: "text-muted" },
};

// Toaster renders the top-centre toast stack. Mounted once near the app root; it
// owns the single toast listener and auto-dismisses each toast.
export function Toaster() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);
  const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  function remove(id: number) {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const tm = timers.current[id];
    if (tm) {
      clearTimeout(tm);
      delete timers.current[id];
    }
  }

  useEffect(() => {
    setToastListener(({ variant, message }) => {
      idRef.current += 1;
      const id = idRef.current;
      setToasts((prev) => [...prev, { id, variant, message }]);
      // Errors linger longer than confirmations.
      timers.current[id] = setTimeout(() => remove(id), variant === "error" ? 6000 : 3500);
    });
    const pending = timers.current;
    return () => {
      setToastListener(null);
      Object.values(pending).forEach(clearTimeout);
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 top-4 z-[60] flex flex-col items-center gap-2 px-4">
      {toasts.map((t) => {
        const v = VARIANTS[t.variant];
        const Icon = v.icon;
        return (
          <div
            key={t.id}
            role="status"
            className={cn(
              "pointer-events-auto flex w-full max-w-md items-start gap-2.5 rounded-xl border px-4 py-3 shadow-xl",
              v.cls,
            )}
          >
            <Icon size={18} className={cn("mt-0.5 shrink-0", v.iconCls)} />
            <span className="min-w-0 flex-1 text-sm font-semibold">{t.message}</span>
            <button onClick={() => remove(t.id)} aria-label="Dismiss" className="shrink-0 opacity-70 transition-opacity hover:opacity-100">
              <X size={16} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
